import { useState } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { cn } from '@/lib/utils'
import { ChevronRight, Folder } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { DOCUMENT_CATEGORY_COLORS, type LibraryFolder } from '@/lib/schemas'

interface FolderSelectTreeProps {
  selectedFolderId: string | null
  onSelect: (folderId: string) => void
  currentFolderId?: string | null | undefined
  /** Folder IDs to hide from the tree (e.g. folder being deleted + its children) */
  excludeIds?: Set<string>
}

export function FolderSelectTree({
  selectedFolderId,
  onSelect,
  currentFolderId,
  excludeIds,
}: FolderSelectTreeProps) {
  const { getRootFolders, getChildFolders } = useLibraryStore()
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const rootFolders = getRootFolders().filter((f) => !excludeIds?.has(f.id))

  const toggleExpand = (folderId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }

  return (
    <ScrollArea className="border rounded-md max-h-64">
      <div className="p-1">
        {/* Folder tree */}
        {rootFolders.map((folder) => (
          <FolderSelectItem
            key={folder.id}
            folder={folder}
            level={0}
            selectedFolderId={selectedFolderId}
            currentFolderId={currentFolderId}
            expandedIds={expandedIds}
            onToggleExpand={toggleExpand}
            onSelect={onSelect}
            getChildFolders={getChildFolders}
            excludeIds={excludeIds}
          />
        ))}

        {rootFolders.length === 0 && (
          <p className="px-3 py-2 text-sm text-muted-foreground">No folders available</p>
        )}
      </div>
    </ScrollArea>
  )
}

interface FolderSelectItemProps {
  folder: LibraryFolder
  level: number
  selectedFolderId: string | null
  currentFolderId?: string | null | undefined
  expandedIds: Set<string>
  onToggleExpand: (folderId: string) => void
  onSelect: (folderId: string) => void
  getChildFolders: (parentId: string | null) => LibraryFolder[]
  excludeIds?: Set<string> | undefined
}

function FolderSelectItem({
  folder,
  level,
  selectedFolderId,
  currentFolderId,
  expandedIds,
  onToggleExpand,
  onSelect,
  getChildFolders,
  excludeIds,
}: FolderSelectItemProps) {
  const children = getChildFolders(folder.id).filter((f) => !excludeIds?.has(f.id))
  const hasChildren = children.length > 0
  const isExpanded = expandedIds.has(folder.id)
  const isSelected = selectedFolderId === folder.id
  const isCurrent = currentFolderId === folder.id

  // Get category color for the dot indicator
  const categoryColor = DOCUMENT_CATEGORY_COLORS[folder.category] ?? 'bg-gray-100 text-gray-800'
  const dotColor = categoryColor.split(' ')[0]

  return (
    <div>
      <button
        type="button"
        onClick={() => onSelect(folder.id)}
        className={cn(
          'w-full flex items-center gap-1 px-2 py-1.5 rounded-md text-sm transition-colors',
          'hover:bg-accent',
          isSelected && 'bg-accent font-medium'
        )}
        style={{ paddingLeft: `${level * 12 + 12}px` }}
      >
        {/* Expand/Collapse Toggle */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onToggleExpand(folder.id)
          }}
          className={cn(
            'h-4 w-4 flex items-center justify-center rounded hover:bg-muted',
            !hasChildren && 'invisible'
          )}
        >
          <ChevronRight
            className={cn(
              'h-3 w-3 transition-transform',
              isExpanded && 'rotate-90'
            )}
          />
        </button>

        {/* Folder Icon */}
        <Folder className="h-4 w-4 flex-shrink-0 text-muted-foreground" />

        {/* Category Color Dot */}
        <span
          className={cn('h-2 w-2 rounded-full flex-shrink-0', dotColor)}
          title={folder.category}
        />

        {/* Folder Name */}
        <span className="flex-1 text-left truncate">{folder.name}</span>

        {/* Current indicator */}
        {isCurrent && (
          <span className="text-meta">(current)</span>
        )}
      </button>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {children.map((child) => (
            <FolderSelectItem
              key={child.id}
              folder={child}
              level={level + 1}
              selectedFolderId={selectedFolderId}
              currentFolderId={currentFolderId}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
              getChildFolders={getChildFolders}
              excludeIds={excludeIds}
            />
          ))}
        </div>
      )}
    </div>
  )
}
